import { httpClient } from "../http-client";
import { extractApiData } from "./response-utils";
import type {
  BillingHistoryResponse,
  BillingPortalSessionResponse,
  CancelSubscriptionDto,
  CancelSubscriptionResponse,
  ChangeTierDto,
  CreateSubscriptionDto,
  CreateSubscriptionResponse,
  DowngradeSubscriptionResponse,
  EventPricingInput,
  EventPricingResult,
  GetCurrentSubscriptionResponse,
  GetSubscriptionTiersResponse,
  PricingConfig,
  TrialStatusResponse,
  UpgradeSubscriptionResponse,
  VerifyPaymentResponse,
  WithdrawalResponse,
} from "../../types/api/subscriptions";

export const subscriptionsApi = {
  getCurrentSubscription: async (): Promise<GetCurrentSubscriptionResponse> => {
    const response = await httpClient.get<GetCurrentSubscriptionResponse>(
      "/subscriptions/current",
    );
    return extractApiData(response);
  },

  getTiers: async (
    segment?: "b2c" | "b2b",
  ): Promise<GetSubscriptionTiersResponse> => {
    const response = await httpClient.get<GetSubscriptionTiersResponse>(
      "/subscriptions/tiers",
      {
        params: segment ? { segment } : undefined,
      },
    );
    return extractApiData(response);
  },

  getTrialStatus: async (): Promise<TrialStatusResponse> => {
    const response = await httpClient.get<TrialStatusResponse>(
      "/subscriptions/trial",
    );
    return extractApiData(response);
  },

  getBillingHistory: async (
    id: string,
    limit?: number,
  ): Promise<BillingHistoryResponse> => {
    const response = await httpClient.get<BillingHistoryResponse>(
      `/subscriptions/${id}/billing`,
      {
        params: typeof limit === "number" ? { limit } : undefined,
      },
    );
    return extractApiData(response);
  },

  createSubscription: async (
    dto: CreateSubscriptionDto,
  ): Promise<CreateSubscriptionResponse> => {
    const response = await httpClient.post<CreateSubscriptionResponse>(
      "/subscriptions",
      dto,
    );
    return extractApiData(response);
  },

  upgradeSubscription: async (
    id: string,
    dto: ChangeTierDto,
  ): Promise<UpgradeSubscriptionResponse> => {
    const response = await httpClient.put<UpgradeSubscriptionResponse>(
      `/subscriptions/${id}/upgrade`,
      dto,
    );
    return extractApiData(response);
  },

  downgradeSubscription: async (
    id: string,
    dto: ChangeTierDto,
  ): Promise<DowngradeSubscriptionResponse> => {
    const response = await httpClient.put<DowngradeSubscriptionResponse>(
      `/subscriptions/${id}/downgrade`,
      dto,
    );
    return extractApiData(response);
  },

  cancelSubscription: async (
    id: string,
    dto: CancelSubscriptionDto,
  ): Promise<CancelSubscriptionResponse> => {
    const response = await httpClient.delete<CancelSubscriptionResponse>(
      `/subscriptions/${id}`,
      { data: dto },
    );
    return extractApiData(response);
  },

  requestWithdrawal: async (id: string): Promise<WithdrawalResponse> => {
    const response = await httpClient.post<WithdrawalResponse>(
      `/subscriptions/${id}/withdraw`,
    );
    return extractApiData(response);
  },

  /**
   * Ссылка на портал Stripe для смены карты. Адрес возврата задаёт СЕРВЕР —
   * принимать его от клиента нельзя (open redirect), поэтому тела у запроса нет.
   */
  createBillingPortalSession:
    async (): Promise<BillingPortalSessionResponse> => {
      const response = await httpClient.post<BillingPortalSessionResponse>(
        "/subscriptions/billing-portal",
      );
      return extractApiData(response);
    },

  verifyPayment: async (): Promise<VerifyPaymentResponse> => {
    const response = await httpClient.post<VerifyPaymentResponse>(
      "/subscriptions/verify-payment",
    );
    return extractApiData(response);
  },

  // ===== TRIAL PAYMENT BARRIER — A/B TEST (FIX-05) =====

  createTrialSetupIntent: async (): Promise<{
    clientSecret: string;
    setupIntentId: string;
    requireCard: boolean;
  }> => {
    const response = await httpClient.post<{
      clientSecret: string;
      setupIntentId: string;
      requireCard: boolean;
    }>("/subscriptions/trial/setup-intent");
    return extractApiData(response);
  },

  activateTrial: async (
    setupIntentId: string,
  ): Promise<{
    message: string;
    eventsRemaining: number;
    requireCard: boolean;
  }> => {
    const response = await httpClient.post<{
      message: string;
      eventsRemaining: number;
      requireCard: boolean;
    }>("/subscriptions/trial/activate", { setupIntentId });
    return extractApiData(response);
  },

  getPricingConfig: async (): Promise<PricingConfig> => {
    const response = await httpClient.get<PricingConfig>(
      "/subscriptions/pricing-config",
    );
    return extractApiData(response);
  },

  calculateEventPrice: async (
    input: EventPricingInput,
  ): Promise<EventPricingResult> => {
    const response = await httpClient.post<EventPricingResult>(
      "/subscriptions/calculate-price",
      input,
    );
    return extractApiData(response);
  },

  getAiUsage: async (): Promise<{
    tokensUsed: number;
    tokensLimit: number;
    tokensRemaining: number;
    percentRemaining: number;
    hasExtraBalance: boolean;
  }> => {
    const response = await httpClient.get<any>("/subscriptions/ai-usage");
    return extractApiData(response);
  },

  getAiPackages: async (): Promise<
    Array<{
      id: string;
      name: string;
      description: string | null;
      tokens: number;
      priceEur: number | string;
      stripePriceId: string;
      isActive: boolean;
      sortOrder: number;
    }>
  > => {
    const response = await httpClient.get<any>("/ai-packages");
    return extractApiData(response);
  },

  createAiPackageCheckout: async (
    packageId: string,
  ): Promise<{ checkoutUrl: string }> => {
    const response = await httpClient.post<any>("/ai-packages/checkout", {
      packageId,
    });
    return extractApiData(response);
  },
};
